buildAuthentication = (credentials) ->
  if credentials.mfaCode
    return {
      url: '/new_two_factor_authentication_challenge',
      body: {
        passcode: credentials.mfaCode
      }
    }
  else
    return {
      url: '/login',
      body: {
        customer: {
          email: credentials.email,
          password: credentials.password
        }
      }
    }

class AuthenticationService
  PERMITTED_SESSION_TIMEOUT_PARAMS = [
    'timeout_action', 'email', 'product_type', 'application_owner'
  ]

  constructor: ($http, $modal, $rootScope, $window, $cookies, $q, $modalStack, ConfigService, ApplicationService) ->
    @$http              = $http
    @$modal             = $modal
    @$rootScope         = $rootScope
    @$window            = $window
    @$cookies           = $cookies
    @$q                 = $q
    @$modalStack        = $modalStack
    @ConfigService      = ConfigService
    @loginPromise       = null
    @ApplicationService = ApplicationService

  setCSRF: (token) ->
    @$http.defaults.headers.common['X-CSRF-TOKEN'] = token

  reauthenticate: () ->
    return @loginPromise if @loginPromise
    @loginPromise = @$modal.open({
      animation: false
      keyboard: false
      backdrop: 'static'
      templateUrl: "/shared/v3/modals/sign_in.html"
      controller: 'LoginModalCtrl'
    }).result
    .then((data) =>
      @loginPromise = null
      this.setCSRF(data.csrf_token)
      angular.extend(@$rootScope, data)
      data
    )

  timeoutSession: (data) ->
    @$rootScope.$broadcast "submitDisabled", true
    params = @session_timeout_params(data)
    switch data?.timeout_action
      when "restart"
        # This is a guest customer who cannot log in, so restart the application
        # from scratch. If the needed params aren't present, this will redirect
        # to the root path
        @$window.location.href = "/restart_application?#{params}"
      when "merchant_redirect"
        @$window.location.href = data?.merchant_url
      when "my_offer"
        @$window.location.href = "/myoffer?#{params}"
      when "in_branch_email_sms"
        # This is distinct from "my_offer" because a different flash message
        # will be rendered
        @$window.location.href = "/myoffer?#{params}"
      when "in_branch_landing"
        @$window.location.href = "/branch?#{params}"
      else
        # Prompt customer to log in again
        @$window.location.href = "/login?#{params}"

  refresh_token: (credentials) ->
    @$http.post(
      '/api/refresh_token',
      {},
      { headers: { 'Accept': 'application/json' }, ignoreLoadingBar: true }
    ).then((response) =>
      data = _.pick(response.data, 'csrf_token', 'api_token', 'admin_api_token', 'client_install_uuid')
      this.setCSRF(data.csrf_token)
      angular.extend(@$rootScope, data)
      data
    )

  check_session_timeout: (options = {}) ->
    # Prevent check from happening on demo pages
    return if @$window.location.pathname.indexOf("/demo") == 0
    return unless @$window.avantConfig.content?.session_timeout_enabled

    @$rootScope.$broadcast "checkingSessionStatus", true
    path = @ConfigService.sessionTimeoutCheckUrl(options.customer_application_id)

    @$http.get(path)
    .then((response) =>
      @$rootScope.$broadcast "checkingSessionStatus", false
      this.display_renew_session_modal(options) if response.data?.warn
    ).catch((error) =>
      @$rootScope.$broadcast "checkingSessionStatus", false
      @$q.reject(error)
    )

  renew_session: => @$http.put('/api/session_timeout')

  logout: (options = {}) =>
    if @ConfigService.canRestartApplication
      appId = options.customer_application_id || @ApplicationService.target_application_id()
      path = @ConfigService.restartApplicationUrl(appId)
      @$http.delete(path)
    else
      @$window.location.replace('/logout')

  authenticate2fa: (credentials) ->
    info = buildAuthentication(credentials);
    this.setCSRF(credentials.csrfToken)
    @$http.put(
      info.url,
      info.body,
      { headers: { Accept: 'application/json' } }
    ).then((response) ->
      _.pick(response.data.session, 'csrf_token', 'api_token', 'admin_api_token', 'client_install_uuid')
    )

  showSessionWillExpireAmountModal: =>
    @$rootScope.$broadcast "amountModal:open", 'session_will_expire', {
      ariaLabel: 'Session will expire',
      renewSession: @renew_session,
      logout: @logout
    }

  display_renew_session_modal: (options) ->
    if @ConfigService.useAmountModalForSessionExpiry
      @showSessionWillExpireAmountModal()
    else
      return @renewSessionModal if @renewSessionModal
      @renewSessionModal = @$modal.open({
        animation: false
        keyboard: false
        backdrop: false # Otherwise it won't work if overlaid on another modal
        # The modals all have identical copy; only the CSS is different, so we can
        # remove the URLs from ConfigService once all apps are on new brand
        templateUrl: @ConfigService.renewSessionModalVersion
        controller: [
          "$scope", "$modalInstance", "HeapService"
          ($scope, $modalInstance, HeapService) ->
            HeapService.track "session_expiration:warning_modal:view"

            $scope.renewSession = => $modalInstance.close(true)
            $scope.logout = => $modalInstance.close(false)
        ]
      })
      @renewSessionModal.result
      .then((result) =>
        @renewSessionModal = null
        if result
          @renew_session()
        else
          @logout(options)
      )

  authenticate: (credentials) ->
    if credentials.mfaCode
      return this.authenticate2fa(credentials)

    @$http.get('/customer/csrf_token').then (response) ->
      response.data.csrf_token
    .then (token) =>
      this.setCSRF(token)
    .then =>
      info = buildAuthentication(credentials)
      @$http.post info.url,
        info.body,
        { headers: { Accept: 'application/json' } }
    .then (response) ->
      if response.client_install_uuid? then @authenticated = true
      _.pick response.data.session, 'csrf_token', 'api_token', 'admin_api_token', 'client_install_uuid'

  resendMFACode: (credentials) ->
    this.setCSRF(credentials.csrfToken)
    @$http.post('/two_factor_authentication_challenge/resend_passcode', null, { headers: { Accept: 'application/json' } })

  session_timeout_params: (data) ->
    Object.entries(data)
          .filter(([k, _v]) => PERMITTED_SESSION_TIMEOUT_PARAMS.includes(k))
          .map(([k, v]) => "#{k}=#{v}")
          .join('&')

  session_timeout_check_interval: 60000 # Check session status every 60 seconds

angular
.module('avant.services')
.service('AuthenticationService',[
  '$http',
  '$modal',
  '$rootScope',
  '$window',
  '$cookies',
  '$q',
  '$modalStack',
  'ConfigService',
  'ApplicationService',
  AuthenticationService
])

if typeof exports != 'undefined'
  if typeof module != 'undefined' && module.exports
    exports = module.exports = AuthenticationService
  exports.AuthenticationService = AuthenticationService
else
  window.AuthenticationService = AuthenticationService
